import _promisescript from 'promisescript';
import { document } from '../env/browser';

export interface ScriptAsset {
  url: string;
  type: 'script' | 'style';
}

export function loadAssetsSequentially(
  urls: string[],
  promisescript: (url: string) => Promise<unknown> = _promisescript
): Promise<unknown[]> {
  return urls.reduce(
    (promise: Promise<unknown[]>, url: string) =>
      promise.then((values) =>
        promisescript(url).then((value) => [...values, value])
      ),
    Promise.resolve([])
  );
}

function injectScript({ url, type }: ScriptAsset): Promise<void> {
  return new Promise((resolve, reject) => {
    const tagName = ({ style: 'link', script: 'script' } as const)[type];
    const node = document.createElement(tagName);

    if (tagName === 'link') {
      (node as HTMLLinkElement).href = url;
      (node as HTMLLinkElement).rel = 'stylesheet';
    } else {
      // Invalidates old cache. This is temporary needed to avoid CORS issues when injecting
      // the crossorigin tag attribute
      (node as HTMLScriptElement).src = `${url}?n=1`;
      (node as HTMLScriptElement).charset = 'utf8';
      node.setAttribute('crossorigin', 'anonymous');
    }

    node.addEventListener('load', () => resolve());
    node.addEventListener('error', (e: Event) => reject(e));
    node.addEventListener('abort', () =>
      reject(new Error(`Script loading aborted for ${url}`))
    );

    document.head.appendChild(node);
  });
}

export function loadAssetsConcurrently(
  assets: ScriptAsset[],
  loadAsset: (asset: ScriptAsset) => Promise<unknown> = injectScript
): Promise<unknown[]> {
  return Promise.all(assets.map((asset) => loadAsset(asset)));
}

export function loadSomeAssetsConcurrently<T>(
  urls: T[],
  promisescript: (urls: T[]) => Promise<unknown>[] = _promisescript
): Promise<(unknown | Error)[]> {
  const promises = promisescript(urls);
  const safePromises = promises.map(
    (promise) =>
      new Promise<unknown | Error>((resolve) =>
        promise.then(
          (value) => resolve(value),
          (error) => resolve(error instanceof Error ? error : new Error(error))
        )
      )
  );

  return Promise.all(safePromises).then((results) => {
    if (results.every((result) => result instanceof Error)) {
      throw new Error('no asset could be loaded');
    }
    return results;
  });
}
